import React, { useContext, useEffect, useRef, useState } from "react";
import { IToastContext, ToastContext } from "../../../providers/Toast";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Tabs, { TabPane } from "rc-tabs";
import { getStandDetail, getStandTeam, trackVisitor } from "../../../context/stands/action";
import StandDetailGetAppointmentTab from "./getAppointment/view";
import StandDetailHomeTab from "./home/container";
import StandDetailProductsTab from "./products/container";
import StandDetailEventsTab from "./events/container";
import StandDetailBrochuresTab from "./brochures/container";
import StandDetailTeamTab from "./team/container";
import VisitorsCounter from "../../../components/stands/visitorsCounter/VisitorsCounter";
import LeaveBusinessCardButton from "../../../components/stands/businessCard/LeaveBusinessCardButton";
import { useDispatch, useSelector } from "react-redux";
import { AuthState } from "../../../context/auth/auth/reducer";
import Breadcrumbs from "../../../components/shared/breadcrumbs/Breadcrumbs";
import { Stand, UserSnippet } from "../../../data/interfaces";
import { STAND_LEVELS, USER_ROLES } from "../../../data/enums";
import ChatButton from "../../../components/stands/chatButton/ChatButton";
import Button from "../../../components/shared/button/Button";
import { StandsState } from "../../../context/stands/reducer";
import _environment from "../../../_environment/_environment";
import { FUNCTIONS } from "../../../firebase/functions";
import { InlineShareButtons } from "sharethis-reactjs";
import { ROUTES } from "../../../routing/routes";
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from "../../../utils/analytics";

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------
interface Props {
  breadcrumbs: any[];
}

interface Tab {
  label: string;
  key: string;
  position: number;
  content: React.ReactNode;
}

const StandDetailView = ({ breadcrumbs, ...restProps }: Props) => {
  // -----------------------------------------------------------------
  // L o c a l   v a r s
  // -----------------------------------------------------------------
  // Reference to Redux store dispatch function: use it to dispatch actions.
  const dispatch = useDispatch();

  // Access to toast manager hook that you may use to show toasts.
  const { openToast } = useContext<IToastContext>(ToastContext);

  // Access to i18n instance that you may use to translate your content.
  const { t } = useTranslation();

  // Returns an object of key/value pairs of URL parameters that you may use to access match.params of the current <Route>.
  let { id } = useParams();

  // -----------------------------------------------------------------
  // N a v i g a t i o n   v a r s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // S t a t e
  // -----------------------------------------------------------------
  const [loading, setLoading] = useState<boolean>(false);
  const [stand, setStand] = useState<Stand | null>(null);

  const [tabs, setTabs] = useState<Tab[]>([]);
  const [activeTabKey, setActiveTabKey] = useState<string>("home");

  const [team, setTeam] = useState<UserSnippet[]>([]);
  const [teamLoaded, setTeamLoaded] = useState<boolean>(false);

  // -----------------------------------------------------------------
  // R e f s  (DOM)
  // -----------------------------------------------------------------
  const appointmentTabRef: any = useRef();

  // -----------------------------------------------------------------
  // u s e S e l e c t o r   m e t h o d s  (redux)
  // -----------------------------------------------------------------
  const { user } = useSelector(({ authReducer }: { authReducer: AuthState }) => {
    return authReducer;
  });

  const { stands } = useSelector(({ standsReducer }: { standsReducer: StandsState }) => {
    return standsReducer;
  });
  // -----------------------------------------------------------------
  // W o r k i n g   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * Load stand from firebase
   * @function
   * @async
   *
   * @param loadedStand
   */
  const initStand = async (loadedStand?: Stand) => {
    if (!loadedStand) {
      setLoading(true);
      try {
        const data = await getStandDetail(id);

        setStand(data);

        // Track event
        trackAnalyticsEvent(ANALYTICS_EVENTS.STAND_VISIT, {
          stand: { id: data.id, name: data.name },
        });
      } catch (err) {
        console.error(err);
        openToast({
          type: "error",
          title: "errors.general.title",
          message: "errors.general.description",
          callback: () => {
          },
        });
      } finally {
        setLoading(false);
      }
    } else {
      setStand(loadedStand);

      // Track event
      trackAnalyticsEvent(ANALYTICS_EVENTS.STAND_VISIT, {
        stand: { id: loadedStand.id, name: loadedStand.name },
      });
    }

    // Track is not here cause the "stand" variable on state may be null at tracking time
  };

  const initTabs = (): void => {
    let res: Tab[] = [];
    if (stand && stand.level >= STAND_LEVELS.BASIC) {
      res = [
        ...res,
        ...[
          {
            label: t("pages.standDetail.tabs.home.title"),
            key: "home",
            content: <StandDetailHomeTab stand={stand}/>,
            position: 0,
          },
        ],
      ];
    }
    if (stand && stand.level >= STAND_LEVELS.ADVANCED) {
      res = [
        ...res,
        ...[
          {
            label: t("pages.standDetail.tabs.events.title"),
            key: "events",
            content: <StandDetailEventsTab stand={stand}/>,
            position: 2,
          },
          {
            label: t("pages.standDetail.tabs.brochure.title"),
            key: "brochure",
            content: <StandDetailBrochuresTab stand={stand}/>,
            position: 3,
          },
          {
            label: t("pages.standDetail.tabs.products.title"),
            key: "products",
            content: <StandDetailProductsTab stand={stand}/>,
            position: 1,
          },
          {
            label: t("pages.standDetail.tabs.team.title"),
            key: "team",
            content: (
              <StandDetailTeamTab
                stand={stand}
                team={team}
                onAppointment={(member: UserSnippet) => {
                  handleTabChange("appointments");
                  if (!!appointmentTabRef && !!appointmentTabRef.current) {
                    appointmentTabRef.current.selectMember(member);
                  }
                }}
              />
            ),
            position: 4,
          },
        ],
      ];

      if (user?.role === USER_ROLES.VISITOR) {
        res = [
          ...res,
          ...[
            {
              label: t("pages.standDetail.tabs.appointments.title"),
              key: "appointments",
              content: (
                <StandDetailGetAppointmentTab stand={stand} team={team} ref={appointmentTabRef}/>
              ),
              position: 5,
            },
          ],
        ];
      }
    }
    setTabs(res.sort((a, b) => a.position - b.position));
  };

  /**
   * Defines active tab
   * @function
   * @async
   *
   * @param tab
   */
  const handleTabChange = async (tab: string) => {
    setActiveTabKey(tab);
    if (stand) {
      switch (tab) {
        case "team":
        case "appointments":
          if (!teamLoaded) {
            const team: UserSnippet[] = await getStandTeam(stand.id);
            setTeam(team);
            setTeamLoaded(true);
          }

          break;
      }
    }
  };

  // -----------------------------------------------------------------
  // R e n d e r   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * Render stand title (name)
   * @function
   */
  const renderTitle = (): React.ReactNode => {
    return stand ? <h1 className={"title"}>{stand.name}</h1> : null;
  };

  /**
   * Renders stand tags
   * @function
   */
  const renderTags = (): React.ReactNode => {
    return stand && stand.tags && stand.tags.length ? (
      <div className={"tags clearfix"}>
        {stand.tags.map((tag: string) => (
          <Link key={tag} to={`${ROUTES.SEARCH}?query=${tag}`} className="tag">
            #{tag}
          </Link>
        ))}
      </div>
    ) : null;
  };

  /**
   * Renders stand logo image
   * @function
   */
  const renderLogo = (): React.ReactNode => {
    return stand ? (
      <a href={stand.logo_image_url} className={"logo"} data-fancybox>
        <div style={{ backgroundImage: `url(${stand.logo_image_url})` }}/>
      </a>
    ) : null;
  };

  /**
   * Render primary actions
   * @function
   */
  const renderPrimaryActions = (): React.ReactNode => {
    return stand && user?.role === USER_ROLES.VISITOR ? (
      <section>
        <h3>{t("pages.standDetail.enjoy")}</h3>
        <div className="primary-actions">
          {stand.level >= STAND_LEVELS.BASIC && <ChatButton stand={stand}/>}
          <LeaveBusinessCardButton stand={stand}/>
          {stand.level >= STAND_LEVELS.ADVANCED && (
            <Button
              className="btn btn-primary btn-outline"
              onClick={() => handleTabChange("appointments")}
            >
              {t("pages.standDetail.bookAnAppointment")}
              <i className="fas fa-calendar-check"></i>
            </Button>
          )}
        </div>
      </section>
    ) : null;
  };

  /**
   * Renders stand main info
   * @function
   */
  const renderInfo = (): React.ReactNode => {
    return stand ? (
      <section>
        <h3>{t("pages.standDetail.about.sections.info.title")}</h3>
        {/* <section className={"categories"}>
                    <strong>{t("pages.standDetail.about.sections.categories.title")}</strong>
                    <ul>
                        {stand.categories.map((category: string) => (
                            <li key={category}>{category}</li>
                        ))}
                    </ul>
                </section> */}
        <section className={"owner"}>
          <strong>{t("pages.standDetail.about.sections.reference.title")}</strong>
          <p>{stand.owner_name}</p>
        </section>
        {stand.website && (
          <section className={"website"}>
            <strong>{t("pages.standDetail.about.sections.website.title")}</strong>
            <p>
              <a href={stand.website} target={"_blank"}>
                {t("pages.standDetail.about.sections.website.label")}
              </a>
            </p>
          </section>
        )}
      </section>
    ) : null;
  };

  /**
   * Renders main tabs
   * @function
   */
  const renderTabs = (): React.ReactNode => {
    return stand && tabs.length > 0 ? (
      <Tabs onChange={handleTabChange} activeKey={activeTabKey}>
        {tabs.map((tab) => (
          <TabPane tab={tab.label} key={tab.key}>
            {tab.content}
          </TabPane>
        ))}
      </Tabs>
    ) : (
      <img
        className="img-responsive sponsor-placeholder"
        src="/assets/images/undraw/sponsor_detail.svg"
      />
    );
  };

  /**
   * Renders social share buttons
   * @function
   */
  const renderSocialShareButtons = (): React.ReactNode => {
    const shareURL = `${_environment.firebase.functionsURL}/${FUNCTIONS.GET_STAND_CONTENT}?id=${stand?.id}`;

    return (
      <section>
        <h3>{t("pages.standDetail.about.share")}</h3>
        <InlineShareButtons
          config={{
            alignment: "left", // alignment of buttons (left, center, right)
            color: "social", // set the color of buttons (social, white)
            enabled: true, // show/hide buttons (true, false)
            font_size: 16, // font size for the buttons
            labels: null, // button labels (cta, counts, null)
            language: "en", // which language to use (see LANGUAGES)
            networks: [
              // which networks to include (see SHARING NETWORKS)
              "whatsapp",
              "linkedin",
              "facebook",
              "twitter",
            ],
            padding: 12, // padding within buttons (INTEGER)
            radius: 100, // the corner radius on each button (INTEGER)
            show_total: false,
            size: 50, // the size of each button (INTEGER)

            // OPTIONAL PARAMETERS
            url: shareURL, // (defaults to current url)
            image: stand?.logo_image_url, // (defaults to og:image or twitter:image)
            description: stand?.description, // (defaults to og:description or twitter:description)
            title: `${stand?.name} | Meeters`, // (defaults to og:title or twitter:title)
            username: "Meeters", // (only for twitter sharing)
          }}
        />
      </section>
    );
    // return (
    //   <section>
    //     <h3>{t("pages.standDetail.about.share")}</h3>
    //     <ul className={"social-share-buttons"}>
    //       <li className={"linkedin"}>
    //         <a
    //           title={"Linkedin"}
    //           onClick={() => {
    //             window.open(
    //               `http://www.linkedin.com/shareArticle?mini=true&url=${shareURL}`,
    //               "",
    //               "left=0,top=0,width=650,height=420,personalbar=0,toolbar=0,scrollbars=0,resizable=0"
    //             );
    //           }}
    //         >
    //           <i className="fab fa-linkedin-in" />
    //         </a>
    //       </li>
    //       <li className={"facebook"}>
    //         <a
    //           title={"Facebook"}
    //           onClick={() => {
    //             //@ts-ignore
    //             FB.ui(
    //               {
    //                 method: "share",
    //                 href: shareURL,
    //               },
    //               (response: any) => {}
    //             );
    //           }}
    //         >
    //           <i className="fab fa-facebook-f" />
    //         </a>
    //       </li>
    //       <li className={"whatsapp"}>
    //         <a
    //           title={"Whatsapp"}
    //           href={`whatsapp://send?text=${shareURL}`}
    //           data-action="share/whatsapp/share"
    //         >
    //           <i className="fab fa-whatsapp" />
    //         </a>
    //       </li>
    //       <li className={"twitter"}>
    //         <a
    //           title={"Twitter"}
    //           onClick={() => {
    //             window.open(
    //               `https://twitter.com/share?url="${shareURL}&text=Meeters - ${stand?.name}`,
    //               "",
    //               "left=0,top=0,width=650,height=420,personalbar=0,toolbar=0,scrollbars=0,resizable=0"
    //             );
    //           }}
    //         >
    //           <i className="fab fa-twitter" />
    //         </a>
    //       </li>
    //     </ul>
    //   </section>
    // );
  };

  // -----------------------------------------------------------------
  // L i f e c y c l e
  // -----------------------------------------------------------------
  /**
   * This method is called the first time the component is mounted
   * @function
   */
  const init = (): void => {
    // init component
    console.log("[StandDetailView] init");
  };

  /**
   * This method is called when the component is unmounted
   * @function
   */
  const destroy = (): void => {
    // destroy component
    console.log("[StandDetailView] destroy");
  };

  // -----------------------------------------------------------------
  // u s e E f f e c t   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * This hook is called once when the component is mounted
   */
  useEffect(() => {
    init();
    return () => {
      destroy();
    };
  }, []);

  /**
   * Hook to stands in order to init stand
   */
  useEffect(() => {
    if (!!stands && stands.length > 0) {
      // Search if already loaded

      let stand = stands.filter((s) => s.id === id)[0];

      if (!!stand) {
        initStand(stand);
      }
    } else {
      initStand();
    }
  }, [stands]);

  /**
   * Hook to stand in order to init tabs
   */
  useEffect(() => {
    if (stand) {
      initTabs();

      if (!!user) {
        if (!user.visited_stands || !user.visited_stands.includes(stand.id)) {
          // Track visited stand
          dispatch(trackVisitor(id, user));
        }
      }
    }
  }, [stand]);

  /**
   * Hook to team in order to init tabs
   */
  useEffect(() => {
    initTabs();
  }, [team]);

  // -----------------------------------------------------------------
  // T e m p l a t e
  // -----------------------------------------------------------------

  return (
    <div>
      {!loading && stand ? (
        <div id={"stand-detail"} className="page-content">
          <header className={"level-" + stand?.level}>
            {stand.level >= STAND_LEVELS.ADVANCED ? (
              <>
                <div
                  className={"cover"}
                  style={{ backgroundImage: `url(${stand.cover_image_url})` }}
                ></div>
                <div className={"container"}>
                  <div className={"content"}>
                    {renderLogo()}
                    <div className="title-wrapper">
                      {renderTitle()}
                      {renderTags()}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className={"container"}>
                {renderLogo()}
                <div className={"info"}>
                  {renderTitle()}
                  {renderTags()}
                </div>
              </div>
            )}

            <div className="container visitors">
              <VisitorsCounter stand={stand}/>
            </div>
          </header>
          <section>
            <div className={"container"}>
              <Breadcrumbs labelReplacers={{ ":standName": stand.name }}/>
              <div className={"row"}>
                <div className={"col-xs-12 col-sm-12 col-md-4 col-lg-4 col-xlg-3"}>
                  <div className={"box description"}>
                    {renderPrimaryActions()}
                    {renderInfo()}
                    {renderSocialShareButtons()}
                  </div>
                </div>
                <div className={"col-xs-12 col-sm-12 col-md-8 col-lg-8 col-xlg-9"}>
                  <div className={"box interact"}>{renderTabs()}</div>
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : (
        <div className="list-loading">
          <i className="fas fa-spin fa-spinner"/>
        </div>
      )}
    </div>
  );
};
export default StandDetailView;
